import React, { useState, useEffect, Fragment } from 'react';

import { Button, Modal, Input, InputNumber, Form, Row, Col, Divider, Select, Typography } from 'antd';

import { uniq, orderBy } from 'lodash';

import {
  getRabbitSignClientOptionNameWithClientPreffix,
  getRabbitSignTemplateClientOptionName,
  getSignerColor,
  mapRabbitSignField,
} from '../../../../../../../shared/utils';

const AssignSignersOrderModal = ({
  visible,
  signers,
  setFields,
  setSignersOrder,
  template,
  isTemplateConfig,
  setIsTemplateConfirm,
  close,
  rootContainer,
}) => {
  const [signersConfigForm] = Form.useForm();
  const [templateSignersArray, setTemplateSignersArray] = useState(null);
  const [alreadyUsedEmails, setAlreadyUsedEmails] = useState([]);

  useEffect(() => {
    if (signers?.length && template?.id && isTemplateConfig) {
      const templateEmails = uniq(
        template?.rabbitSignFieldSettings?.map((obj) => {
          return { email: obj.email, contactType: obj.contactType };
        }),
      );

      const templateSigners = uniq(
        orderBy(templateEmails, ['contactType'], ['asc']).map((obj, index) => {
          return {
            email: obj.email,
            contactType: obj.contactType,
            signerColor: getSignerColor(index),
            signerLabel: 'Client ' + (index + 1),
          };
        }),
      );

      const chosenEmails = [];

      signers.forEach((signer, index) => {
        const chosen = templateSigners.find((obj) => obj.signerLabel === signer.signerLabel)?.email;

        if (chosen) {
          chosenEmails.push(chosen);
        }
      });

      setAlreadyUsedEmails(chosenEmails);

      setTemplateSignersArray(templateSigners);
    } else {
      setTemplateSignersArray(null);
    }
  }, []);

  const assign = () => {
    signersConfigForm.validateFields().then((values) => {
      let drawIndex = 0;

      const newFields = [];
      const newSigners = [];

      values?.signers?.forEach((formListElement, formListIndex) => {
        const settings = template?.rabbitSignFieldSettings?.filter((obj) => obj.email === formListElement.settingByEmail);

        const newSigner = { ...signers[formListIndex], ...{ signingOrder: formListElement.signingOrder } };

        newSigners.push(newSigner);

        if (isTemplateConfig && settings?.length) {
          settings.forEach((element, elIndex) => {
            const propsToOverride = {
              contactType: signers[formListIndex].contactType,
              email: formListElement.email,
              signingOrder: formListElement.signingOrder,
            };

            const newfield = mapRabbitSignField(element, ++drawIndex, propsToOverride);

            newFields.push(newfield);
          });
        }
      });

      setSignersOrder(newSigners);

      if (isTemplateConfig) {
        setFields(newFields);
        setIsTemplateConfirm(false);
      }

      signersConfigForm.resetFields();
      close();
    });
  };

  const cancel = () => {
    signersConfigForm.resetFields();
    close();
  };

  const onFormChange = (allValues) => {
    const chosenEmails = [];
    allValues?.signers.forEach((element) => {
      if (element.settingByEmail) {
        chosenEmails.push(element.settingByEmail);
      }
    });
    setAlreadyUsedEmails(chosenEmails);
  };

  return (
    <div id="signing-order-modal-wrap">
      <Modal
        title={templateSignersArray?.length ? 'Configure template' : 'Configure signing order'}
        id="signing-order-modal"
        centered
        destroyOnClose={true}
        open={visible}
        onCancel={() => cancel()}
        onOk={() => assign()}
        width={1000}
        maskClosable={false}
        footer={[
          <Button key="signing-order-back" onClick={() => cancel()}>
            Cancel
          </Button>,
          <Button key="signing-order-button" type="primary" onClick={() => assign()}>
            Save
          </Button>,
        ]}
        getContainer={() => document.getElementById('signing-order-modal-wrap')}
      >
        <Row key={'signer-order-header'} span={24}>
          <Col span={templateSignersArray?.length ? 12 : 20}>
            <Typography.Text strong>Client</Typography.Text>
          </Col>
          {templateSignersArray?.length && <Col span={8}> </Col>}
          <Col span={4}>
            <Typography.Text strong>Signing Order</Typography.Text>
          </Col>
        </Row>
        <Divider key="signer-order-header-divider" type="horizontal" orientation="left" style={{ marginBottom: '5px', marginTop: '5px' }} />

        <Form form={signersConfigForm} id="signer-configuration-form" onValuesChange={(_, allValues) => onFormChange(allValues)}>
          <Form.List name="signers" initialValue={signers}>
            {(fields) =>
              fields.map((field, index) => (
                <Fragment key={'fragment' + index}>
                  <Row key={'signer-row-' + index} span={24}>
                    <Col span={templateSignersArray?.length ? 12 : 20} key={'signer-email-' + index}>
                      {getRabbitSignClientOptionNameWithClientPreffix(signers[index])}
                      <Form.Item
                        noStyle
                        {...field}
                        key={'signer-template-email-item-' + index}
                        name={[field.name, 'email']}
                        initialValue={signers[index].email}
                        hidden={true}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    {templateSignersArray?.length && (
                      <Col span={8} key={'signer-template-email-col' + index}>
                        <Form.Item
                          {...field}
                          key={'signer-template-email-item-' + index}
                          name={[field.name, 'settingByEmail']}
                          rules={[{ required: true, message: 'This field is required.' }]}
                          style={{ marginLeft: '10%' }}
                          initialValue={
                            templateSignersArray.find((obj) => obj.signerLabel === signers[index].signerLabel)?.email
                          }
                        >
                          <Select
                            allowClear={true}
                            id={'signer-template-email-' + index}
                            key={'signer-template-email-' + index}
                            placeholder="Choose client from template"
                            title="Choose client from template"
                            getPopupContainer={rootContainer}
                            style={{ width: '80%' }}
                          >
                            {templateSignersArray.map((obj, optIndex) => {
                              const optName = getRabbitSignTemplateClientOptionName(obj, 'signer-template-email-option-'+ optIndex);

                              return (
                                <Select.Option
                                  getContainer={() => document.getElementById('signer-template-email-' + index)}
                                  key={'template-signer-' + index + '-' + optIndex + obj.email}
                                  disabled={alreadyUsedEmails.includes(obj.email)}
                                  value={obj.email}
                                  title={obj.email}
                                >
                                  {optName}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </Form.Item>
                      </Col>
                    )}

                    <Col span={4} key={'signer-signingOrder-' + index}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'signingOrder']}
                        noStyle
                        initialValue={signers[index].signingOrder}
                        key={'signer-order-input-item-' + index}
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <InputNumber
                          key={'signer-order-input-' + index}
                          placeholder="Signing Order"
                          min={1}
                          step={1}
                          max={signers.length}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {index < signers.length - 1 && (
                    <Divider key={'sigrer-divider-' + index} type="horizontal" orientation="left" style={{ marginBottom: '5px', marginTop: '5px' }} />
                  )}
                </Fragment>
              ))
            }
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default AssignSignersOrderModal;
